/**
 * 目的: 该js主要解决用户刷新浏览器后之前历史打开的模块页签丢失
 * 标识：GLOBAL_VISITIED_VIEWS
 * 利用 beforeunload 刷新前钩子函数存储已经打开的模块页签
 * 利用 reloadRoutes 记录哪些路由切换时需要重新加载
 */

import Router from '@/layout/router/index.js'
import { ENV } from '@/public/config'
import store from '@/public/store'
import bus from 'public/utils/bus'
import { resolveRouteByPath, filterTabs } from './tabs-utils.js'
import DefaultOpenStore from './default-open-tabs.js'

const VISITIED_VIEWS = 'GLOBAL_VISITIED_VIEWS'

const reloadRoutes = {}

const routeName = {} // 由于路由加载问题，没有加载业务线路由信息的时候，name不存在

// 判断非远程包（主包）才在window上挂载
if (!process.env.IS_REMOTE_MODULE) {
  window.__ERP_reloadRoutes = reloadRoutes
}

// 移除路由状态
function removeRouteStatus (path) {
  delete reloadRoutes[path]
}

function setRouteStatus (path) {
  reloadRoutes[path] = 1
}

function setMetaLayout (route) {
  route.meta.layout = false
}

function setRouteName (route) {
  routeName[route.path] = route.name
}

function getRouteName (path) {
  return routeName[path]
}

function getMenuIdByRoute (path) {
  const route = resolveRouteByPath(path)

  const menu = route && route.meta && store.getters.menus[route.meta.tag]
  if (menu) {
    return menu
  }

  return null
}

// 上报用户打开模块时间
function reportMenuOpenTime (path) {
  const menu = getMenuIdByRoute(path)
  const route = resolveRouteByPath(path)

  if (!menu) {
    return
  }
  const menuId = menu.id
  const tag = route.meta.tag
  const title = menu.title || route.meta.title
  const time = Date.now() + ''

  store.state.tagsView.logs[menuId] = time
  bus.$emit('GLOBAL_MENU_OPEN', { tag, menuId, title, time })
}

// 存储页签缓存
function storeTabs () {
  const tabs = filterTabs(store.state.tagsView.visitedViews, getRouteName)
  sessionStorage.setItem(VISITIED_VIEWS, JSON.stringify(tabs))
}

// 过滤无权限菜单
function filterAuthTabs (data) {
  const menus = store.state.menus
  return data.filter(route => route.meta && route.meta.tag && menus[route.meta.tag])
}

// 获取页签缓存
function getTabs () {
  let data = []
  try {
    data = sessionStorage.getItem(VISITIED_VIEWS)
    data = JSON.parse(data) || []
  } catch (error) {
    data = []
  }
  return filterAuthTabs(data)
}

// 删除页签缓存
function deleteTags () {
  sessionStorage.removeItem(VISITIED_VIEWS)
}

// 页面是否在iframe里面
function isIframe () {
  // 判断当前的窗口是不是顶层窗口，如果为false，则代码是在iframe内执行
  return window.self !== window.top
}

// 设置模块状态，标识是否是用户主动打开
function setUserOpenModule (tag) {
  bus.$emit('GLOBAL_USE_OPEN_MODULE', tag)
}

function setModuleStatus (path, tag) {
  if (reloadRoutes[tag]) {
    setUserOpenModule(tag)
  }
}

// 刷新浏览器时，存储页签缓存
function onUnload () {
  storeTabs()
}

function onAfterEach (to, from) {
  try {
    // 当前页签存在的情况下，页签切换时 meta.layout 会被设置为true
    // 如果此处不设置 false, 会导致缓存页签不会重新请求接口
    if (reloadRoutes[to.meta.tag]) {
      setMetaLayout(to)
      removeRouteStatus(to.meta.tag)
      reportMenuOpenTime(to.meta.tag)
      console.info('%c*****ERP-Reload:路由信息:', 'color: #8b60f0', reloadRoutes, to.meta.layout)
    }
  } catch (e) {
    console.error('[erp-reload]', e)
  }
}

// 初始化页签状态，判断是否需要变更 meta.layout
function initRouteStatus (data) {
  data.forEach(route => {
    if (route && route.meta) {
      setRouteStatus(route.meta.tag)
    }
  })
}


// 铸剑初始化时，初始化页签
function initPageTabs () {
  const data = getTabs()
  if (Array.isArray(data)) {
    initRouteStatus(data)
    data.forEach(route => {
      setRouteName(route)
      store.dispatch('addVisitedViews', route)
    })
  }
  deleteTags()
  console.info('%c*****ERP-Reload:sessionStorage:', 'color: #8b60f0', data)
  // 获取默认打开的页签
  DefaultOpenStore.getDefaultOpenTabs()
}

function initEvent () {
  // 页面刷新前记录页签信息
  window.addEventListener('unload', onUnload)

  // 监听路由用来重置 meta.layout
  Router.afterEach(onAfterEach)

  // 页签切换时，需要主动标记页签是用户主动打开
  bus.$on('TAB_CLICK', setModuleStatus)
}

// 控制功能是否在环境生效
function useKeepTabsByEnv (env) {
  // 本地环境
  if (!env) {
    return true
  }

  const lookup = window.__ERPLOOKUP__ ? window.__ERPLOOKUP__.find(x => x.code === 'sys_layout_tags_switch') : null
  const envMap = lookup && lookup.value
  // 数据字典不存在情况
  if (!envMap) {
    return true
  }

  const config = envMap.find(e => e.lookupCode === env) || {}
  return config.lookupValue && +config.lookupValue === 1
}

// 判断是否是同一个账号
function useDiffAccount () {
  return window.__ERP__diffAccount__ === true
}


// 判断是否是外部用户，即从其他系统调转到erp
// 账号借用场景：在刷新浏览器的时候，如果用户选择取消，此时缓存会存到 session 里面，使用 window.open 大部分场景会把同域下的 seesion，带入到新页签
function outsideAccount () {
  return window.__ERP__outsideAccount === true
}

function init () {
  if (!useKeepTabsByEnv(ENV)) {
    return
  }

  if (isIframe()) {
    return
  }

  initEvent()

  // 账号不同，用户权限不同，用户可显示菜单不同
  if (useDiffAccount() || outsideAccount()) {
    deleteTags()
  } else {
    initPageTabs()
  }
}

bus.$once('APP_LOADED', init)



// WEBPACK FOOTER //
// ./src/layout/layout/setup/keep-tabs.js